import { useState, useRef, useEffect } from 'react';

const useDragAndDropSort = ({
  items,
  onSort,
  onMove,
  containerRef,
  keyExtractor = (item) => item.id,
  canDrag = () => true,
  dragThreshold = 0, // Порог смещения в пикселях (по умолчанию 5)
  deleteZoneRef,
  onDelete,
  setIsOverDeleteZone,
  ungroupZoneRef,
  onUngroup,
  setIsOverUngroupZone,
  ungroupZoneTopRef,
  onUngroupTop,
  setIsOverUngroupZoneTop,
  needToStopPropagation = false,
}) => {
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [dragDirection, setDragDirection] = useState(null);
  const [ghostPosition, setGhostPosition] = useState({ x: 0, y: 0 });
  const [isDraggingActive, setIsDraggingActive] = useState(false); // Флаг активного перетаскивания
  const itemRefs = useRef({});
  const startPos = useRef({ x: 0, y: 0 }); // Начальная позиция указателя
  const actualPos = useRef({ x: 0, y: 0 }); // Начальная позиция указателя
  const touchDelayTimeout = useRef(null);

  const handlePointerDownToStartDrag = (e, itemId) => {
    if (!canDrag(itemId)) return;

    if (needToStopPropagation) {
      e.preventDefault();
      e.stopPropagation();
    }

    const element = itemRefs.current[itemId];
    if (!element) return;

    // Сохраняем начальную позицию указателя
    startPos.current = { x: e.clientX, y: e.clientY };
    actualPos.current = { x: e.clientX, y: e.clientY };

    const startDrag = () => {
       // Вычисляем смещение от начальной позиции
       const deltaX = Math.abs(actualPos.current.x - startPos.current.x);
       const deltaY = Math.abs(actualPos.current.y - startPos.current.y);

       if (e.pointerType === 'touch' && (deltaX > 15 || deltaY > 15)) { return; }

       setDraggingId(itemId);
       setIsDraggingActive(true);
       setGhostPosition({ x: actualPos.current.x, y: actualPos.current.y });
       window.croco?.haptic?.(); // Виброотклик при начале перетаскивания
    }

    if (e.pointerType === 'touch') {
      // Очищаем предыдущий таймаут, если он есть
      if (touchDelayTimeout.current) {
        clearTimeout(touchDelayTimeout.current);
      }
      
      // Устанавливаем задержку для touch
      touchDelayTimeout.current = setTimeout(startDrag, 300); // 300мс задержка

    } else {

      setDraggingId(itemId);

    }

    document.body.style.userSelect = 'none';
  };

  const handlePointerMove = (e) => {

    actualPos.current = { x: e.clientX, y: e.clientY };

    if (!draggingId) return;

    // e.preventDefault();
    // e.stopPropagation();

    // Вычисляем смещение от начальной позиции
    const deltaX = Math.abs(e.clientX - startPos.current.x);
    const deltaY = Math.abs(e.clientY - startPos.current.y);
    const containerRect = containerRef.current.getBoundingClientRect();
    const localX = e.clientX - containerRect.left;
    const localY = e.clientY - containerRect.top;

    // Если смещение превысило порог, активируем перетаскивание
    if (!isDraggingActive && (deltaX > dragThreshold || deltaY > dragThreshold)) {
      setIsDraggingActive(true);
      setGhostPosition({ x: e.clientX, y: e.clientY });
    }

    // Если перетаскивание активно, обновляем позицию "призрака"
    if (isDraggingActive) {
      setGhostPosition({ x: e.clientX, y: e.clientY });

      if (deleteZoneRef?.current) {
        const deleteRect = deleteZoneRef.current.getBoundingClientRect();
        const isOver = e.clientX >= deleteRect.left && e.clientX <= deleteRect.right &&
                       e.clientY >= deleteRect.top && e.clientY <= deleteRect.bottom;
        setIsOverDeleteZone?.(isOver);
      }

      if (ungroupZoneRef?.current) {
        const ungroupRect = ungroupZoneRef.current.getBoundingClientRect();
        const isOver = e.clientX >= ungroupRect.left && e.clientX <= ungroupRect.right &&
                       e.clientY >= ungroupRect.top && e.clientY <= ungroupRect.bottom;
        setIsOverUngroupZone?.(isOver);
      }

      if (ungroupZoneTopRef?.current) {
        const ungroupTopRect = ungroupZoneTopRef.current.getBoundingClientRect();
        const isOver = e.clientX >= ungroupTopRect.left && e.clientX <= ungroupTopRect.right &&
                       e.clientY >= ungroupTopRect.top && e.clientY <= ungroupTopRect.bottom;
        setIsOverUngroupZoneTop?.(isOver);
      }

      const elements = document.elementsFromPoint(e.clientX, e.clientY);
      const draggableItem = elements.find(el => el.dataset.draggableId);

      if (draggableItem) {
        const targetId = draggableItem.dataset.draggableId;

        if (targetId && targetId !== draggingId) {
          setDragOverId(targetId);

          const fromIndex = items.findIndex(item => keyExtractor(item) === draggingId);
          const toIndex = items.findIndex(item => keyExtractor(item) === targetId);
          setDragDirection(fromIndex < toIndex ? 'down' : 'up');

        } else {
          setDragOverId(null);
          setDragDirection(null);
        }
      } else {
        setDragOverId(null);
        setDragDirection(null);
      }
    }
  };

  const handlePointerUp = (e) => {
    if (touchDelayTimeout.current) {
      clearTimeout(touchDelayTimeout.current);
    }

    if (!draggingId) return;
    
    e.preventDefault();
    e.stopPropagation();


    if (deleteZoneRef?.current) {
      const deleteRect = deleteZoneRef.current.getBoundingClientRect();
      if (e.clientX >= deleteRect.left && e.clientX <= deleteRect.right &&
          e.clientY >= deleteRect.top && e.clientY <= deleteRect.bottom) {
        onDelete?.(draggingId);
      }
    }

    if (ungroupZoneRef?.current) {
      const ungroupRect = ungroupZoneRef.current.getBoundingClientRect();
      if (e.clientX >= ungroupRect.left && e.clientX <= ungroupRect.right &&
          e.clientY >= ungroupRect.top && e.clientY <= ungroupRect.bottom) {
        onUngroup?.(draggingId);
      }
    }

    if (ungroupZoneTopRef?.current) {
      const ungroupTopRect = ungroupZoneTopRef.current.getBoundingClientRect();
      if (e.clientX >= ungroupTopRect.left && e.clientX <= ungroupTopRect.right &&
          e.clientY >= ungroupTopRect.top && e.clientY <= ungroupTopRect.bottom) {
        onUngroupTop?.(draggingId);
      }
    }

    // Если перетаскивание было активно и есть целевой элемент
    if (isDraggingActive && dragOverId && dragOverId !== draggingId) {
       // Сначала проверяем, не является ли это перемещением НА элемент
      if (onMove && onMove(draggingId, dragOverId)) {
        // onMove вернул true, значит он обработал событие. Сортировка не нужна.
      } else if (onSort) {
        const sorted = [...items];
        const fromIndex = sorted.findIndex(item => keyExtractor(item) === draggingId);
        const toIndex = sorted.findIndex(item => keyExtractor(item) === dragOverId);
  
        if (fromIndex !== -1 && toIndex !== -1) {
          const [movedItem] = sorted.splice(fromIndex, 1);
          sorted.splice(toIndex, 0, movedItem);
  
          const updatedItems = sorted.map((item, idx) => ({
            ...item,
            order: sorted.length - idx - 1,
          }));
  
          onSort(updatedItems);
        }
      }
    }

    setDraggingId(null);
    setDragOverId(null);
    setDragDirection(null);
    setIsDraggingActive(false);
    setGhostPosition({ x: 0, y: 0 });
    setIsOverDeleteZone?.(false);
    setIsOverUngroupZone?.(false);
    setIsOverUngroupZoneTop?.(false);
    document.body.style.userSelect = '';
  };

  useEffect(() => {
    document.addEventListener('pointermove', handlePointerMove, { passive: false });
    document.addEventListener('pointerup', handlePointerUp, { passive: false });
    document.addEventListener('pointercancel', handlePointerUp, { passive: false });

    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [items, draggingId, dragOverId, isDraggingActive, onSort, onMove]);

  return {
    draggingId,
    dragOverId,
    dragDirection,
    ghostPosition,
    itemRefs,
    handlePointerDownToStartDrag,
    isDraggingActive, // Возвращаем флаг, чтобы можно было использовать его для стилей
  };
};

export default useDragAndDropSort;